import { useRef, useState } from "react";
import { Pencil, Power, Trash2 } from "lucide-react";
import { Banner, Button, Field, Modal, StatusDot, TextArea, Timeline, useToast } from "@/shared/rd";
import { Actions, Eyebrow, Meta, SectionCard } from "@/shared/stores/components/rd-local";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  capturePrivateSession,
  isPrivateSessionCurrent,
} from "@/lib/api/private-session";
import { formatDate } from "@/lib/formatting/date-time";
import type { AdminStoreAuditView, StoreView } from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import {
  useStoreControlsQuery,
  useStoreLifecycleMutation,
} from "@/modules/stores/services/storeControls";
import { StoreDetailsEditModal } from "@/modules/stores/components/StoreDetailsEditModal";
import { StoreDeleteModal } from "@/modules/stores/components/StoreDeleteModal";
import { StoreForceDeleteModal } from "@/modules/stores/components/StoreForceDeleteModal";

type Props = {
  store: StoreView;
  locale: string;
  /** ADMIN / SUPER_ADMIN: edit, deactivate, reactivate (ManagingAdminGuard). */
  canManage: boolean;
  /** SUPER_ADMIN: delete forever and force delete (SuperAdminGuard). */
  canDelete: boolean;
  /** After a delete (its toast already showing): leave the record. */
  onDeleted: () => void;
};

type LifecycleAction = "deactivate" | "reactivate";

/**
 * The store's status and the admin controls over it (founder decisions
 * 2026-10-25): edit details / web address, deactivate / reactivate with an
 * internal reason, and — in a danger zone — delete forever, which is only
 * offered for a store with no real activity. When activity blocks it, a super
 * admin gets a second "Force delete" that erases the store anyway, offered
 * only once the store is deactivated. Every admin sees the status and
 * the history (Support needs to know a store is off, and why); the buttons
 * follow the same role split the API enforces.
 */
export function StoreControlsCard({ store, locale, canManage, canDelete, onDeleted }: Props) {
  const t = useTranslations();
  const toast = useToast();
  const lifetime = useRef(capturePrivateSession()).current;
  const { data } = useStoreControlsQuery(store.id);
  const lifecycle = useStoreLifecycleMutation();
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [forceDeleting, setForceDeleting] = useState(false);
  const [pending, setPending] = useState<LifecycleAction | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  /** A failed Undo: said on the card, where the status it was about sits. */
  const [undoError, setUndoError] = useState<LifecycleAction | null>(null);

  const deactivated = data?.deactivation ?? null;

  const describeResult = (action: LifecycleAction, revalidated: boolean) =>
    revalidated
      ? t(action === "deactivate" ? keys.storeControls.deactivated : keys.storeControls.reactivated)
      : t(keys.storeControls.appliedDelayed);

  // Deactivate and reactivate undo each other through the API, so the answer
  // is a toast with Undo. The modal stays only to take the internal reason.
  const undo = async (action: LifecycleAction) => {
    setUndoError(null);
    try {
      const result = await lifecycle.mutateAsync({ storeId: store.id, action, reason: null });
      if (!isPrivateSessionCurrent(lifetime)) return;
      toast.show({ tone: "neutral", message: describeResult(action, result.revalidated) });
    } catch {
      if (!isPrivateSessionCurrent(lifetime)) return;
      setUndoError(action);
    }
  };

  const applyLifecycle = async () => {
    if (!pending) return;
    setError(null);
    setUndoError(null);
    const action = pending;
    try {
      const result = await lifecycle.mutateAsync({
        storeId: store.id,
        action,
        reason: reason.trim() || null,
      });
      if (!isPrivateSessionCurrent(lifetime)) return;
      const inverse: LifecycleAction = action === "deactivate" ? "reactivate" : "deactivate";
      toast.show({
        tone: "neutral",
        message: describeResult(action, result.revalidated),
        actionLabel: t("admin.undo"),
        onAction: () => void undo(inverse),
      });
      setPending(null);
      setReason("");
    } catch (caught) {
      if (!isPrivateSessionCurrent(lifetime)) return;
      setError(
        caught instanceof ApiJsonClientError && caught.message
          ? caught.message
          : t(keys.errors.generic),
      );
    }
  };

  const titleId = `store-controls-${store.id}`;

  return (
    <SectionCard
      badge="gear"
      title={t(keys.storeControls.title)}
      titleId={titleId}
      note={
        deactivated ? (
          <>
            {t(keys.storeControls.deactivatedBy, {
              name: deactivated.byName ?? t(keys.storeControls.unknownAdmin),
              date: formatDate(deactivated.at, locale),
            })}
            {deactivated.reason ? ` — ${deactivated.reason}` : ""}
          </>
        ) : null
      }
      aside={
        data ? (
          <span data-status={deactivated ? "deactivated" : "live"}>
            <StatusDot tone={deactivated ? "red" : "green"}>
              {t(deactivated ? keys.storeControls.statusDeactivated : keys.storeControls.statusLive)}
            </StatusDot>
          </span>
        ) : null
      }
    >
      {canManage ? (
        <Actions>
          <Button size="sm" icon={Pencil} onClick={() => setEditing(true)} disabled={!data}>
            {t(keys.storeControls.edit)}
          </Button>
          <Button
            size="sm"
            icon={Power}
            disabled={!data}
            onClick={() => {
              setError(null);
              setPending(deactivated ? "reactivate" : "deactivate");
            }}
          >
            {t(deactivated ? keys.storeControls.reactivate : keys.storeControls.deactivate)}
          </Button>
        </Actions>
      ) : null}

      {undoError ? (
        <Banner
          tone="error"
          title={t(undoError === "deactivate" ? "storeControls.deactivateFailedTitle" : "storeControls.reactivateFailedTitle")}
          actions={
            <Button size="sm" loading={lifecycle.isPending} loadingLabel={t("admin.busyTrying")} onClick={() => void undo(undoError)}>
              {t(keys.shell.retry)}
            </Button>
          }
          onDismiss={() => setUndoError(null)}
          dismissLabel={t(keys.common.close)}
        >
          {t("admin.nothingChanged")}
        </Banner>
      ) : null}

      {data?.audits?.length ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Eyebrow>{t(keys.storeControls.historyTitle)}</Eyebrow>
          <Timeline
            items={data.audits.slice(0, 5).map((audit) => ({
              id: audit.id,
              time: formatDate(audit.createdAt, locale),
              tone: audit.action === "DEACTIVATE" ? "bad" : audit.action === "REACTIVATE" ? "good" : "ink",
              filled: true,
              label: t(keys.storeControls.history[audit.action]),
              note: describeAudit(audit, t) || undefined,
            }))}
          />
        </div>
      ) : null}

      {canManage && data?.deletion ? (
        <div
          data-testid="store-danger-zone"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            paddingTop: 12,
            borderTop: "1px solid var(--rd-line-row)",
          }}
        >
          <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--rd-red)" }}>
            {t(keys.storeControls.dangerZone)}
          </span>
          <Meta>{t(keys.storeControls.deleteDesc)}</Meta>
          {!data.deletion.allowed ? (
            <Banner tone="warning" title={t(keys.storeControls.deleteBlocked)}>
              {data.deletion.blockers.map((blocker) => t(keys.storeControls.blocker[blocker])).join(" · ")}
            </Banner>
          ) : !canDelete ? (
            <Meta>{t(keys.storeControls.deleteSuperOnly)}</Meta>
          ) : null}
          <Actions>
            <Button
              size="sm"
              variant="danger"
              icon={Trash2}
              disabled={!canDelete || !data.deletion.allowed}
              onClick={() => setDeleting(true)}
            >
              {t(keys.storeControls.deleteForever)}
            </Button>
          </Actions>
          {canDelete && !data.deletion.allowed && data.forceDelete ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="store-force-delete">
              <Meta>{t(keys.storeControls.forceDeleteDesc)}</Meta>
              {data.forceDelete.refusals.includes("STORE_FORCE_DELETE_REQUIRES_DEACTIVATION") ? (
                <Meta data-refusal="deactivate-first">{t(keys.storeControls.forceDeleteNeedsDeactivation)}</Meta>
              ) : null}
              {data.forceDelete.refusals.includes("STORE_FORCE_DELETE_CUSTOM_DOMAIN") ? (
                <Meta data-refusal="custom-domain">{t(keys.storeControls.forceDeleteNeedsDomainRemoval)}</Meta>
              ) : null}
              <Actions>
                <Button
                  size="sm"
                  variant="danger"
                  disabled={!data.forceDelete.allowed}
                  onClick={() => setForceDeleting(true)}
                >
                  {t(keys.storeControls.forceDelete)}
                </Button>
              </Actions>
            </div>
          ) : null}
        </div>
      ) : null}

      {editing && data ? (
        <StoreDetailsEditModal
          open
          onClose={() => setEditing(false)}
          onSaved={(message) => toast.show({ message })}
          store={store}
          qrTableCount={data.qrTableCount}
        />
      ) : null}

      {deleting ? (
        <StoreDeleteModal
          open
          onClose={() => setDeleting(false)}
          storeId={store.id}
          storeSlug={store.storeSlug}
          onDeleted={onDeleted}
        />
      ) : null}

      {forceDeleting && data?.forceDelete ? (
        <StoreForceDeleteModal
          open
          onClose={() => setForceDeleting(false)}
          storeId={store.id}
          storeSlug={store.storeSlug}
          counts={data.forceDelete.counts}
          onDeleted={onDeleted}
        />
      ) : null}

      <Modal
        open={pending !== null}
        onClose={lifecycle.isPending ? undefined : () => setPending(null)}
        title={t(pending === "reactivate" ? keys.storeControls.reactivateTitle : keys.storeControls.deactivateTitle)}
        actions={
          <>
            <Button variant="ghost" muted onClick={() => setPending(null)} disabled={lifecycle.isPending}>
              {t(keys.common.cancel)}
            </Button>
            <Button
              variant={pending === "deactivate" ? "danger" : "primary"}
              loading={lifecycle.isPending}
              loadingLabel={t(pending === "reactivate" ? "admin.busyReactivating" : "admin.busyDeactivating")}
              onClick={() => void applyLifecycle()}
            >
              {t(pending === "reactivate" ? keys.storeControls.reactivate : keys.storeControls.deactivate)}
            </Button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {error ? (
            <Banner
              tone="error"
              title={t(pending === "reactivate" ? "storeControls.reactivateFailedTitle" : "storeControls.deactivateFailedTitle")}
            >
              {error} {t("admin.nothingChanged")}
            </Banner>
          ) : null}
          <p className="rd-modal-text" style={{ margin: 0 }}>
            {t(pending === "reactivate" ? keys.storeControls.reactivateBody : keys.storeControls.deactivateBody)}
          </p>
          <Field label={t(keys.storeControls.reason)}>
            <TextArea
              name="lifecycleReason"
              rows={2}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
        </div>
      </Modal>
    </SectionCard>
  );
}

/** The line under an audit entry: who, the rename, and the reason. */
function describeAudit(audit: AdminStoreAuditView, t: ReturnType<typeof useTranslations>): string {
  const parts: string[] = [];
  if (audit.adminName) parts.push(audit.adminName);
  const from = audit.details.slugFrom;
  const to = audit.details.slugTo;
  if (audit.action === "RENAME" && typeof from === "string" && typeof to === "string") {
    parts.push(t(keys.storeControls.historyRename, { from, to }));
  }
  if (audit.reason) parts.push(audit.reason);
  return parts.join(" · ");
}
